export type EmbedProvider = 'youtube' | 'vimeo' | 'drive';

const YT_ID = /^[A-Za-z0-9_-]{11}$/;
const DRIVE_ID = /^[A-Za-z0-9_-]{10,100}$/;
const YT_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com']);

/** The provider and video/file id of a URL the board can embed, or null (any other URL becomes a link card). */
export function parseEmbedUrl(url: string): { provider: EmbedProvider; id: string } | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  const host = u.hostname.toLowerCase();
  const parts = u.pathname.split('/').filter(Boolean);
  if (host === 'youtu.be') return YT_ID.test(parts[0] ?? '') ? { provider: 'youtube', id: parts[0]! } : null;
  if (YT_HOSTS.has(host)) {
    const id = parts[0] === 'watch' ? u.searchParams.get('v') : parts[0] === 'shorts' || parts[0] === 'embed' ? parts[1] : null;
    return id && YT_ID.test(id) ? { provider: 'youtube', id } : null;
  }
  if (host === 'vimeo.com' || host === 'www.vimeo.com') return /^\d{1,20}$/.test(parts[0] ?? '') && parts.length === 1 ? { provider: 'vimeo', id: parts[0]! } : null;
  if (host === 'player.vimeo.com') return parts[0] === 'video' && /^\d{1,20}$/.test(parts[1] ?? '') ? { provider: 'vimeo', id: parts[1]! } : null;
  if (host === 'drive.google.com' && parts[0] === 'file' && parts[1] === 'd' && DRIVE_ID.test(parts[2] ?? '')) return { provider: 'drive', id: parts[2]! };
  return null;
}

/** The iframe source the app builds itself (spec §6): never a URL the user typed. */
export function embedSrc(provider: EmbedProvider, id: string): string {
  if (provider === 'youtube') return `https://www.youtube-nocookie.com/embed/${id}`;
  if (provider === 'vimeo') return `https://player.vimeo.com/video/${id}`;
  return `https://drive.google.com/file/d/${id}/preview`;
}
